import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import CopilotDemo from "@/components/od/demo/CopilotDemo";
import { MaskedLine } from "@/components/od/Hero";
import { REVENUE_COPILOT } from "@/data/demoData";

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to see sold vs delivered and what needs attention, with Revenue Copilot",
  description:
    "Ask a plain question; the copilot reads your CRM and projects, surfaces priorities, and prepares the follow-ups — in seconds.",
  step: [
    { "@type": "HowToStep", position: 1, name: "Pick your seat", text: "Owner, sales, or project manager — each role asks its own question." },
    { "@type": "HowToStep", position: 2, name: "Ask the question", text: "The copilot reads live deals, activities, projects, tasks, and timesheets through 27 purpose-built tools." },
    { "@type": "HowToStep", position: 3, name: "Act on the answer", text: "Priorities arrive as a short list with follow-up tasks and drafts already prepared." },
  ],
};

export default function RevenueDemoPage() {
  useEffect(() => {
    document.title = "Revenue Copilot Demo — Sold vs Delivered in Seconds | Ocean Data";
  }, []);

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]">
      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <Link
            to="/demos"
            data-testid="revenue-demo-back"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-teal mb-10 hover:text-brand-navy transition-colors"
          >
            <ArrowLeft size={15} /> All demos
          </Link>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Live demo · sales &amp; projects · sample data
          </p>
          <h1
            data-testid="revenue-demo-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-3xl sm:text-4xl lg:text-5xl mb-6"
          >
            <MaskedLine delay={0.15} testId="revenue-demo-headline-line-1">Sold versus delivered.</MaskedLine>
            <MaskedLine delay={0.3} testId="revenue-demo-headline-line-2">
              <span className="text-brand-teal">Answered in seconds, not meetings.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed mb-12"
          >
            Pick a seat, tap its weekly question, and watch the copilot read
            the whole record — deals, activities, projects, tasks, timesheets —
            then hand back a short answer with the follow-ups already prepared.
          </motion.p>

          <CopilotDemo copilot={REVENUE_COPILOT} />

          <p className="mt-10 text-sm text-slate-500 leading-relaxed max-w-2xl">
            Under the hood, the copilot works through 27 purpose-built tools
            connected to Zoho CRM and Zoho Projects — reading, writing, and
            acting in real time. You never see any of that. You see your
            morning back.{" "}
            <Link to="/solutions" className="font-semibold text-brand-teal hover:text-brand-navy transition-colors">
              How the CONNECT service works →
            </Link>
          </p>
        </div>
      </section>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />
    </main>
  );
}
